import { useCallback, useEffect, useState } from "react";
import Slider from "@components/Slider";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "@/utils/predictions";
import { Chip } from "@scripts/react/components/Chip";
import { getLabelColor } from "@/components/annotation/colors";
import { BuildPromptSteps } from "@/types/flows";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { useModel } from "@/contexts/ModelContext";

export const ConfidenceSlider = ({
    initialConfidenceThreshold,
    onConfidenceThresholdChanged,
    name,
    predictionCount,
    mode,
    showCount = true,
    className = ""
}: {
    initialConfidenceThreshold?: number;
    onConfidenceThresholdChanged?: (confidenceThreshold: number) => void;
    name?: string;
    predictionCount?: number;
    mode?: BuildPromptSteps;
    showCount?: boolean;
    className?: string;
}) => {
    const { promptLabels } = useModel();
    const [confidenceThreshold, setConfidenceThreshold] = useState(
        initialConfidenceThreshold || DEFAULT_CONFIDENCE_THRESHOLD
    );

    useEffect(() => {
        // initialConfidenceThreshold is not guaranteed to be defined from the first render,
        // lets update it if it changes and the confidenceThreshold is set to the default
        if (
            initialConfidenceThreshold !== undefined &&
            confidenceThreshold === DEFAULT_CONFIDENCE_THRESHOLD
        ) {
            setConfidenceThreshold(initialConfidenceThreshold);
        }
    }, [initialConfidenceThreshold, confidenceThreshold]);

    const handleSliderChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newThreshold = 1 - Number(e.target.value) / 100;

            setConfidenceThreshold(newThreshold);
            onConfidenceThresholdChanged?.(newThreshold);
        },
        [onConfidenceThresholdChanged]
    );

    const value = Math.round((1 - confidenceThreshold) * 100);
    const labelColor = getLabelColor(promptLabels, name || "object");

    return (
        <div className={twClassNames("flex flex-shrink-0 flex-col gap-3 px-4", className)}>
            <div className="flex items-center justify-between text-sm font-medium text-secondary">
                {showCount ? (
                    <>
                        Objects
                        {/* @ts-expect-error - Chip component type issue */}
                        <Chip
                            type="number"
                            customColor={true}
                            className="font-semibold"
                            style={{
                                backgroundColor: `${labelColor}1A`, // 10% opacity
                                color: labelColor
                            }}
                        >
                            {predictionCount}
                            <i
                                className={
                                    mode === BuildPromptSteps.REVIEW_PREDICTIONS
                                        ? "far fa-vector-square"
                                        : "far fa-square-dashed"
                                }
                            ></i>
                        </Chip>
                    </>
                ) : (
                    <>
                        <span>Less</span>
                        <span>More</span>
                    </>
                )}
            </div>
            <div
                className="flex flex-1 items-center gap-3 pb-0.5 text-tertiary"
                style={{ touchAction: "none" }}
            >
                <Slider
                    min={0}
                    max={100}
                    value={value}
                    onChange={handleSliderChange}
                    className="flex-1"
                    showRange={false}
                    step={0.05}
                    sliderClassName="gap-8"
                />
            </div>
        </div>
    );
};
